// The only analytics import path screens and components should use — never
// import posthog/datadog modules directly.
import {
  getConfigFeatureFlag,
  getConfigVariantFlag,
  isEmbedded,
  type LoginFeatureFlagKey,
  type LoginVariantFlagKey,
} from '../../config/runtimeConfig';

import {
  posthogTrackEvent,
  posthogFlush,
  posthogTrackScreen,
  posthogTrackError,
  posthogGetFeatureFlag,
  posthogIsFeatureFlagEnabled,
} from './posthog';
import {
  datadogTrackEvent,
  datadogTrackScreen,
  datadogTrackError,
} from './datadog';

export const analytics = {
  trackEvent(name: string, properties?: Record<string, any>): void {
    posthogTrackEvent(name, properties);
    datadogTrackEvent(name, properties);
  },
  // Call before navigating the page away; bounded so a slow PostHog can't hold it up.
  async flush(): Promise<void> {
    await posthogFlush();
  },

  // Route-change tracking belongs to the host under embed. `datadogTrackScreen`
  // self-guards on isEmbedded() (it's a public subpath export and needs its
  // own defense), but `posthogTrackScreen` does not — the reason $pageview
  // stays silent under embed today is that `AnalyticsProvider` skips
  // `AutoCaptureScreen`. Keep a facade-level guard so a future direct
  // `analytics.trackScreen()` call can't bypass that upstream skip and
  // start double-firing $pageview against the host's identified session.
  trackScreen(name: string, properties?: Record<string, any>): void {
    if (isEmbedded()) return;
    posthogTrackScreen(name, properties);
    datadogTrackScreen(name);
  },

  trackError(error: Error | string, context?: Record<string, any>): void {
    posthogTrackError(error, context);
    datadogTrackError(error, context);
  },

  // Host override wins; falls through to PostHog when unset.
  async isFeatureFlagEnabled(key: LoginFeatureFlagKey): Promise<boolean> {
    const override = getConfigFeatureFlag(key);
    if (override !== undefined) return override;
    return posthogIsFeatureFlagEnabled(key);
  },

  async getFeatureFlag(key: LoginVariantFlagKey): Promise<string | boolean | undefined> {
    const override = getConfigVariantFlag(key);
    if (override !== undefined) return override;
    return posthogGetFeatureFlag(key);
  },
};
